import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import { useEffect, useState } from "react";
import type { branch } from "@/interface/branch.interface";
import { useNavigate } from "react-router-dom";
import { RoutesLink } from "@/utility/RouteLinks";
import {
  Pencil,
  Plus,
  Trash,
  TriangleAlert,
  MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BranchService } from "@/services/Branch/BranchSevice";
import { ThrowError } from "@/api/callApi";
import { GetFilterUrl, showToastNotification } from "@/utility/helper";
import { Button } from "@/components/ui/button";
import CommonModal from "@/components/common/CommonModal";
import type { IFilter, IPageInfo } from "@/interface/common.interface";
import TablePagination from "@/components/common/TablePagination";
import CommonFilter from "@/components/common/CommonFilter";

const Branch: React.FC = () => {
  const navigate = useNavigate();
  const [branch, setBranch] = useState<branch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");
  const [filter, setFilter] = useState<IFilter>({
    search: "",
  });

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const handleSearchChange = (value: string) => {
    setFilter((prev) => {
      if (prev.search === value) return prev;
      return { ...prev, search: value };
    });
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const handleGetBranch = async (
    currentPage = paginationData.currentPage,
    currentLimit = paginationData.pageSize,
  ) => {
    setIsLoading(true);
    try {
      const apiFilter = {
        ...filter,
      };
      const paramsURL = GetFilterUrl(apiFilter);
      const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${paramsURL ? `&${paramsURL}` : ""}`;
      const response = await BranchService?.getBranch(PATH);
      if (response?.status) {
        setBranch(response?.data?.branch || []);
        setPaginationData((prev) => ({
          ...prev,
          total: response?.data?.pageInfo?.total || 0,
          totalPages: response?.data?.pageInfo?.totalPages || 0,
        }));
      } else {
        showToastNotification("danger", response?.message);
      }

    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleGetBranch(paginationData.currentPage, paginationData.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationData.currentPage, paginationData.pageSize, filter]);

  const toggleDeleteDialog = (id: string | undefined) => {
    setDeleteId(id ?? "");
    setDeleteDialogOpen(!deleteDialogOpen);
  };

  const handleDeleteBranch = async () => {
    try {
      const response = await BranchService?.deleteBranch(deleteId);
      if (response?.status) {
        showToastNotification("success", response?.message);
        toggleDeleteDialog("");
        handleGetBranch();
      } else {
        showToastNotification("danger", response?.message);
      }
    } catch (error) {
      ThrowError(error);
    }
  };

  const columns: TableColumn<branch>[] = [
    {
      key: "srNo",
      header: "Sr No",
      render: (_item: branch, index: number) => (
        <span className="text-muted-foreground font-medium"># {index + 1}</span>
      ),
    },

    {
      key: "aliasId",
      header: "Alias ID",
      render: (item: branch) => (
        <span className="font-semibold capitalize">{item?.aliasId || "N/A"}</span>
      )
    },
    {
      key: "name",
      header: "Name",
      render: (item: branch) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shadow-sm">
            {item?.name?.charAt(0)?.toUpperCase() || "B"}
          </div>
          <span className="font-semibold capitalize">{item?.name}</span>
        </div>
      ),
    },
    {
      key: "departmentName",
      header: "Department Name",
      render: (item: branch) => (
        <span className="font-medium text-gray-700 capitalize">
          {item?.name}
        </span>
      ),
    },
    {
      key: "phone",
      header: "Phone",
      render: (item: branch) => (
        <span className="text-gray-600">{item?.phone || "-"}</span>
      ),
    },
    {
      key: "gstNumber",
      header: "GST Number",
      render: (item: branch) => (
        <span className="text-gray-600">{item?.gst || "-"}</span>
      ),
    },
    {
      key: "panNumber",
      header: "PAN Number",
      render: (item: branch) => (
        <span className="text-gray-600">{item?.pan || "-"}</span>
      ),
    },
    {
      key: "tanNumber",
      header: "TAN Number",
      render: (item: branch) => (
        <span className="text-gray-600">{item?.tANDc || "-"}</span>
      ),
    },
    {
      key: "secondaryPhone",
      header: "Secondary Phone",
      render: (item: branch) => (
        <span className="text-gray-600">{item?.secondaryPhone || "-"}</span>
      ),
    },
    {
      key: "secondaryEmail",
      header: "Secondary Email",
      render: (item: branch) => (
        <span className="text-gray-600">{item?.secondaryEmail || "-"}</span>
      ),
    },
    {
      key: "shortName",
      header: "Short Name",
      render: (item: branch) => (
        <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium border border-gray-200">
          {item?.shortName || "-"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item: branch) => {
        const isActive = item?.status === true;
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${isActive ? "text-green-600 border-green-600 bg-green-50" : "text-destructive border-destructive bg-destructive/10"}`}
          >
            {isActive ? "True" : "False"}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      render: (item: branch) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4 text-slate-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-[170px] rounded-xl shadow-lg border-slate-100"
          >
            <DropdownMenuItem
              onClick={() => navigate(`${RoutesLink.editBranch}/${item?.id}`)}
              className="cursor-pointer py-2 px-3 hover:bg-slate-50 focus:bg-slate-50"
            >
              <Pencil className="h-4 w-4" />
              Edit Branch
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => toggleDeleteDialog(item?.id)}
              className="cursor-pointer py-2 px-3 hover:bg-slate-50 focus:bg-slate-50 text-red-600 focus:text-red-600"
            >
              <Trash className="h-4 w-4" />
              Delete Branch
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];
  return (
    <>
      <Layout>
        <div className="space-y-6">
          <MainHeading
            title="Branch"
            buttonText="Add Branch"
            buttonIcon={<Plus />}
            onClick={() => navigate(RoutesLink.addBranch)}
          />
          <CommonFilter
            searchValue={filter.search || ""}
            onSearchChange={handleSearchChange}
            NoAdvanceFilters={true}
          />
          <CommonTable data={branch} columns={columns} loading={isLoading} />
          <TablePagination
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            handleChangePageSize={handleChangePageSize}
            isLoading={isLoading}
          />
        </div>
      </Layout>
      <CommonModal
        title=""
        show={deleteDialogOpen}
        onClose={() => toggleDeleteDialog("")}
        className="max-w-md rounded-2xl p-0 overflow-hidden"
      >
        <div className="p-6">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <TriangleAlert className="text-red-600 w-8 h-8" />
            </div>

            <h2 className="mt-5 text-2xl font-bold text-gray-900">
              Delete Branch?
            </h2>

            <p className="mt-3 text-sm leading-6 text-gray-500">
              This action cannot be undone. Once deleted, all branch data will
              be permanently removed from our servers.
            </p>
          </div>

          <div className="mt-8 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => toggleDeleteDialog("")}
            >
              Cancel
            </Button>

            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleDeleteBranch}
            >
              Delete
            </Button>
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default Branch;
